
// app.states.ts
import { createFeatureSelector, ActionReducerMap, createSelector } from '@ngrx/store';
import * as fromAuth from '../store/reducers/auth.reducers';
import * as fromBrand from '../store/reducers/brand.reducers';

export interface AppState {
  auth: fromAuth.State;
  brand: fromBrand.State;
}

export const reducers: ActionReducerMap<AppState> = {
  auth: fromAuth.reducer,
  brand: fromBrand.reducer,
};

// Just a memoization to avoid computation
export const selectAuthState = createFeatureSelector<fromAuth.State>('auth');
export const selectBrandState = createFeatureSelector<fromBrand.State>('brand');

// ─── Brand selectors ─────────────────────────────────────────────────────────
export const selectAccessibleBrands = createSelector(
  selectBrandState,
  (s) => s.brands,
);

export const selectActiveBrandId = createSelector(
  selectBrandState,
  (s) => s.activeBrandId,
);

export const selectActiveBrand = createSelector(
  selectBrandState,
  (s) => s.brands.find((b) => b.id === s.activeBrandId) ?? null,
);

export const selectHasMultipleBrands = createSelector(
  selectBrandState,
  (s) => s.brands.length > 1,
);

export const selectBrandsLoaded = createSelector(
  selectBrandState,
  (s) => s.loaded,
);

// Selector to get the current user
export const selectCurrentUser = createSelector(
  selectAuthState,
  (authState) => authState.user
);

// This user's own store assignments (user_stores) — e.g. to flag "My Store"
// in per-store views. See User.storeIds for how this differs from a
// permission's brand/store scoping.
export const selectCurrentUserStoreIds = createSelector(
  selectCurrentUser,
  (user) => user?.storeIds || []
);

// Memoized selector factory for checking if user is module admin for a specific resource
export const selectIsModuleAdmin = (resourceCode: string) => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.resourceCode === resourceCode);
    } else {
      // If permissions is a map, access directly
      permission = user.permissions[resourceCode];
    }
    
    const isAdmin = permission ? permission.moduleAccessType === 1 : false;
    return isAdmin;
  }
);

// Memoized selector factory for checking if user is module user for a specific resource
export const selectIsModuleUser = (resourceCode: string) => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.resourceCode === resourceCode);
    } else {
      // If permissions is a map, access directly
      permission = user.permissions[resourceCode];
    }
    
    const isUser = permission ? permission.moduleAccessType === 2 : false;
    return isUser;
  }
);

// Memoized selector factory for checking if user has access to any role in specific resource
export const selectHasAccessToModule = (resourceCode: string) => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.resourceCode === resourceCode);
    } else {
      // If permissions is a map, access directly
      permission = user.permissions[resourceCode];
    }
    
    const hasAccess = !!permission;
    return hasAccess;
  }
);

/** Resolve a per-brand permission flag for the active brand (mirrors backend hasBrandPermission). */
const hasBrandPermissionFlag = (value: unknown, brandId: number | null): boolean => {
  if (value === true) return true;
  if (!value || typeof value !== 'object') return false;
  const map = value as Record<string, boolean>;
  // Unrestricted (all brands) — valid before the brand switcher finishes loading.
  if (map['*']) return true;
  if (brandId == null) return false;
  const key = String(brandId);
  return !!map[key];
};

// Memoized selector factory for checking a granular permission flag (e.g.
// `canManageCA`) beyond plain module access. Org admins always pass.
// Action flags may be per-brand maps — resolve against the active brand.
export const selectHasModuleAction = (resourceCode: string, action: string) => createSelector(
  selectCurrentUser,
  selectActiveBrandId,
  (user, activeBrandId) => {

    if (!user) {
      return false;
    }

    if (user.isOrgAdmin) {
      return true;
    }

    if (!user.permissions) {
      return false;
    }

    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      permission = user.permissions.find(p => p.resourceCode === resourceCode);
    } else {
      permission = user.permissions[resourceCode];
    }

    return hasBrandPermissionFlag(permission?.[action], activeBrandId);
  }
);

// Convenience wrappers around selectHasModuleAction for the 4 standard CRUD
// flags — the common case for gating buttons/routes. Use selectHasModuleAction
// directly for a module's special flags (e.g. Client Module's 'canManageCA').
export const selectCanCreate = (resourceCode: string) => selectHasModuleAction(resourceCode, 'canCreate');
export const selectCanRead = (resourceCode: string) => selectHasModuleAction(resourceCode, 'canRead');
export const selectCanUpdate = (resourceCode: string) => selectHasModuleAction(resourceCode, 'canUpdate');
export const selectCanDelete = (resourceCode: string) => selectHasModuleAction(resourceCode, 'canDelete');

// Memoized selector factory for checking if user is mail controller
export const selectIsMailController = () => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.moduleAccessType == 6);
    } else {
      // If permissions is a map, access directly
      for (const value of Object.values(user.permissions)) {
        if(value.moduleAccessType == 6) {
          permission = value;
          break;
        }
      }
    }
    
    const isMailController = !!permission;
    return isMailController;
  }
);

// Memoized selector factory for checking if user is parcel admin
export const selectIsParcelAdmin = () => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.moduleAccessType == 7);
    } else {
      // If permissions is a map, access directly
      for (const value of Object.values(user.permissions)) {
        if(value.moduleAccessType == 7) {
          permission = value;
          break;
        }
      }
    }
    
    if(user.isOrgAdmin) {
      return true;
    }
    
    const isParcelAdmin = !!permission;
    return isParcelAdmin;
  }
);


// Memoized selector factory for checking if user is reviewer
export const selectIsReviewer = () => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.moduleAccessType == 8);
    } else {
      // If permissions is a map, access directly
      for (const value of Object.values(user.permissions)) {
        if(value.moduleAccessType == 8) {
          permission = value;
          break;
        }
      }
    }
    
    // if(user.isOrgAdmin) {
    //   return true;
    // }
    
    const isParcelAdmin = !!permission;
    return isParcelAdmin;
  }
);


// Memoized selector factory for checking if user is technician
export const selectIsPMTechnician = () => createSelector(
  selectCurrentUser,
  (user) => {
  
    if (!user || !user.permissions) {
      return false;
    }
    
    // Handle both array format (from API) and map format
    let permission;
    if (Array.isArray(user.permissions)) {
      // If permissions is still an array, find by resourceCode
      permission = user.permissions.find(p => p.resourceCode === 'pm' && p.moduleAccessType == 5);
    } else {
      // If permissions is a map, access directly
      permission = user.permissions['pm'];

      if(permission.moduleAccessType != 5) {
        permission = null;
      }
    }
    
    const isTechnician = !!permission;
    return isTechnician;
  }
);